import type { ApiClient } from '@classgo/app-kit';
import { queryOptions } from '@tanstack/react-query';

import type { MentoriaSession, SessionMessage, StreamDone, Topic } from './types';

/**
 * The MentorIA endpoints behind the API and the streaming function URL.
 */
export const mentoriaQueries = {
  topics: (api: ApiClient, gradeId: string, subjectId: string) =>
    queryOptions({
      queryKey: ['mentoria', 'topics', gradeId, subjectId],
      queryFn: () =>
        api.request<Topic[]>(
          `/mentoria/topics?${new URLSearchParams({ gradeId, subjectId }).toString()}`,
        ),
      enabled: gradeId !== '' && subjectId !== '',
    }),
  session: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['mentoria', 'sessions', id],
      queryFn: () =>
        api.request<{ session: MentoriaSession; messages: SessionMessage[] }>(
          `/mentoria/sessions/${id}`,
        ),
      enabled: id !== '',
    }),
};

/**
 * Opens a session for a student in a subject and topic.
 *
 * @param {ApiClient} api -- The client.
 * @param {object} body -- Student, subject and topic.
 * @returns The session.
 */
export const openSession = (
  api: ApiClient,
  body: { studentUserId: string; subjectId: string; topic: string },
) => api.request<MentoriaSession>('/mentoria/sessions', { method: 'POST', body });

/**
 * Sends one message and reads the answer as it streams (server-sent events over a fetch body).
 *
 * @param {object} input -- Stream URL, bearer, session, content and the delta callback.
 * @returns The final event, with usage, cost and remaining quota.
 */
export const streamTurn = async (input: {
  streamUrl: string;
  token: string;
  actAsSession: string | null;
  sessionId: string;
  content: string;
  onDelta: (text: string) => void;
}): Promise<StreamDone> => {
  const response = await fetch(
    `${input.streamUrl.replace(/\/$/, '')}/mentoria/sessions/${input.sessionId}/messages`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${input.token}`,
        'content-type': 'application/json',
        ...(input.actAsSession ? { 'x-act-as-session': input.actAsSession } : {}),
      },
      body: JSON.stringify({ content: input.content }),
    },
  );
  if (!response.ok || !response.body) {
    throw new Error(`MentorIA respondió ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done: StreamDone | null = null;
  const handle = (chunk: string) => {
    buffer += chunk;
    let index = buffer.indexOf('\n\n');
    while (index >= 0) {
      const raw = buffer.slice(0, index);
      buffer = buffer.slice(index + 2);
      const dataLine = raw.split('\n').find((line) => line.startsWith('data: '));
      if (dataLine) {
        const event = JSON.parse(dataLine.slice(6)) as
          | { event: 'delta'; text: string }
          | ({ event: 'done' } & StreamDone)
          | { event: 'error'; code: string; message: string };
        if (event.event === 'delta') input.onDelta(event.text);
        else if (event.event === 'done') done = event;
        else throw Object.assign(new Error(event.message), { code: event.code });
      }
      index = buffer.indexOf('\n\n');
    }
  };
  for (;;) {
    const { value, done: finished } = await reader.read();
    if (finished) break;
    handle(decoder.decode(value, { stream: true }));
  }
  handle(decoder.decode());
  if (!done) throw new Error('MentorIA no terminó la respuesta');
  return done;
};
